/**
 * Hook for AI-powered model recommendations with fallback to scoring system
 */

import { useState, useCallback } from 'react';
import { scoreModels, generateRecommendationSummary, type ModelScore } from '@/lib/modelScoring';
import type { WizardPersona, WizardEnvironment } from '@/stores/wizardStore';
import { toast } from './use-toast';

export interface ModelRecommendation {
  modelId: string;
  score: number;
  reasoning: string[];
  strengths: string[];
  considerations: string[];
  rank: number;
}

export interface RecommendationResult {
  recommendations: ModelRecommendation[];
  summary: string;
  source: 'ai' | 'scoring';
}

interface ModelCatalogItem {
  model_id: string;
  display_name: string;
  params_b: number;
  family: string;
  meta?: {
    category?: string;
    modality?: string;
  };
  benchmarks?: {
    mmlu?: number;
    humaneval?: number;
    gpqa?: number;
    arena_elo?: number;
  };
}

export function useModelRecommendations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getRecommendations = useCallback(
    async (
      models: ModelCatalogItem[],
      environment: WizardEnvironment,
      personas: WizardPersona[]
    ): Promise<RecommendationResult> => {
      setLoading(true);
      setError(null);

      try {
        // Try AI-powered recommendations first
        const aiResult = await getAIRecommendations(models, environment, personas);
        
        if (aiResult) {
          setLoading(false);
          return {
            ...aiResult,
            source: 'ai',
          };
        }
      } catch (err) {
        console.info('AI recommendations unavailable, using scoring system:', err);
        // Fall through to scoring system
      }

      // Fallback to scoring system
      try {
        const scores = scoreModels(models, environment, personas);
        
        let summary = '';
        try {
          summary = generateRecommendationSummary(scores.slice(0, 5), environment, personas);
        } catch (e) {
          console.warn('Failed to generate recommendation summary, continuing without it:', e);
          summary = 'Model recommendations generated based on benchmark data and your configuration.';
        }
        
        const recommendations: ModelRecommendation[] = scores.slice(0, 10).map((score, index) => ({
          ...score,
          rank: index + 1,
        }));

        setLoading(false);
        return {
          recommendations,
          summary,
          source: 'scoring',
        };
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate recommendations');
        setLoading(false);
        throw err;
      }
    },
    []
  );

  return {
    getRecommendations,
    loading,
    error,
  };
}

/**
 * Attempt to get AI-powered recommendations from Lovable AI
 * Returns null if AI is not available or fails
 */
async function getAIRecommendations(
  models: ModelCatalogItem[],
  environment: WizardEnvironment,
  personas: WizardPersona[]
): Promise<Omit<RecommendationResult, 'source'> | null> {
  try {
    // Check if we have the necessary environment variables
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      return null;
    }

    // Prepare context for AI
    const context = {
      environment: {
        totalUsers: environment.totalActiveUsersPerDay,
        concurrentUsers: environment.concurrentUsers,
        latencyTolerance: environment.latencyTolerance,
        dataSensitivity: environment.regulatorySensitivity,
        turns: environment.turns,
        batch: environment.batch,
        precision: environment.precision,
      },
      personas: personas.map(p => ({
        name: p.name,
        role: p.role,
        turnsPerSession: p.turnsPerSession,
        expertiseType: p.expertiseType,
        inTok: p.inTok,
        outTok: p.outTok,
      })),
      availableModels: models.map(m => ({
        id: m.model_id,
        name: m.display_name,
        size: m.params_b,
        family: m.family,
      })),
    };

    const prompt = `You are an AI infrastructure expert. Analyze this configuration and recommend the top 5 models.

Context: ${JSON.stringify(context, null, 2)}

Provide recommendations in this JSON format:
{
  "recommendations": [
    {
      "modelId": "model_id",
      "score": 85,
      "reasoning": ["reason1", "reason2"],
      "strengths": ["strength1", "strength2"],
      "considerations": ["consideration1"],
      "rank": 1
    }
  ],
  "summary": "Brief summary explaining the recommendations"
}

Focus on: latency requirements, throughput needs, data sensitivity, deployment constraints, and cost efficiency.`;

    // Try to call AI endpoint (this will fail gracefully if not set up)
    const response = await fetch(`${supabaseUrl}/functions/v1/ai-model-recommendations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ prompt }),
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data;
  } catch (err) {
    // Silently fail and return null to trigger fallback
    console.debug('AI recommendations not available:', err);
    return null;
  }
}
